import { h } from 'preact';

import { roundCoordinate } from '../../lib/fishCatch';

// Card pieces shared by BOTH /fangst (fishCatch) and /kontakt (fishContact)
// cards. The two differ only in their headline function, their leading icon and
// their badge — everything below is identical, so it lives here rather than in
// two copies.

// yr.no/met.no symbol codes arrive suffixed with _day/_night (e.g.
// 'partlycloudy_day'); matching on `includes` covers both variants with one
// icon per weather family.
export function weatherIconClass(symbolCode) {
    if (!symbolCode) {
        return 'fa-duotone fa-cloud';
    }
    const code = String(symbolCode).toLowerCase();
    if (code.includes('thunder')) return 'fa-duotone fa-cloud-bolt';
    if (code.includes('snow') || code.includes('sleet')) return 'fa-duotone fa-snowflake';
    if (code.includes('rain') || code.includes('drizzle')) return 'fa-duotone fa-cloud-rain';
    if (code.includes('fog')) return 'fa-duotone fa-smog';
    if (code.includes('clearsky')) return 'fa-duotone fa-sun';
    if (code.includes('fair') || code.includes('partlycloudy')) return 'fa-duotone fa-cloud-sun';
    return 'fa-duotone fa-cloud';
}

// The ' · '-separated weather / water-temp / fly / equipment / place row.
// Returns null when the entry has nothing to show, so the caller can render it
// unconditionally.
export function EntryDetails({ entry }) {
    if (!entry) {
        return null;
    }
    const {
        weather, waterTemperature, fly, equipment, placeName,
    } = entry;

    // Air temperature is a real reading and can legitimately be 0°C or
    // negative, so a truthy/`> 0` check would either hit the literal-"0" JSX
    // bug or wrongly hide valid cold-weather entries. Number.isFinite checks
    // presence without relying on truthiness either way.
    const hasWeatherTemp = !!weather && Number.isFinite(weather.temperature);
    // Water temperature (unlike air temperature) is never meaningfully 0 for a
    // real entry, so an explicit `> 0` guard both filters out "not recorded"
    // and avoids rendering a literal 0. This asymmetry with the air-temperature
    // guard above is deliberate — both are pinned by the card tests.
    const hasWaterTemp = waterTemperature > 0;

    const details = [];
    if (hasWeatherTemp) {
        details.push(
            <span key='weather'>
                <i class={`${weatherIconClass(weather.symbolCode)} me-1`} />
                {weather.temperature}°C
            </span>,
        );
    }
    if (hasWaterTemp) {
        details.push(
            <span key='waterTemperature'>
                <i class='fa-duotone fa-water me-1' />
                {waterTemperature}°C
            </span>,
        );
    }
    if (fly) {
        details.push(
            <span key='fly'>
                <i class='fa-duotone fa-feather me-1' />
                {fly}
            </span>,
        );
    }
    if (equipment) {
        details.push(
            <span key='equipment'>
                <i class='fa-duotone fa-reel me-1' />
                {equipment}
            </span>,
        );
    }
    if (placeName) {
        details.push(
            <span key='placeName'>
                <i class='fa-duotone fa-location-dot me-1' />
                {placeName}
            </span>,
        );
    }
    if (details.length === 0) {
        return null;
    }
    const detailsWithSeparators = details.reduce((acc, detail, idx) => {
        if (idx > 0) {
            acc.push(<span key={`sep-${detail.key}`}> · </span>);
        }
        acc.push(detail);
        return acc;
    }, []);

    return (
        <div class='text-body-secondary small mt-1'>
            {detailsWithSeparators}
        </div>
    );
}

// The position row. Exact coordinates are always stored (the weather lookup
// needs them) but must never be shown unless the owner opted in via
// showExactPosition. Rounding happens here, at render time, from the stored
// exact value — the stored data itself is never touched. The backend enforces
// the same rule in lib/fish-catch-privacy.js.
export function EntryPosition({ entry }) {
    if (!entry) {
        return null;
    }
    const { latitude, longitude, showExactPosition } = entry;
    const hasPosition = latitude !== undefined && latitude !== null && latitude !== ''
        && longitude !== undefined && longitude !== null && longitude !== '';
    if (!hasPosition) {
        return null;
    }
    const displayLatitude = showExactPosition === true ? latitude : roundCoordinate(latitude);
    const displayLongitude = showExactPosition === true ? longitude : roundCoordinate(longitude);

    return (
        <div class='text-body-secondary small mt-1'>
            <i class='fa-duotone fa-map-location-dot me-1' />
            {displayLatitude}, {displayLongitude}
        </div>
    );
}
